import React, { useState } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";
import userAPI from "../../api/UserApi";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { handleApiValidationError } from "../../utils/apiErrorHandler";

function ForgotPasswordCanvas() {
  useAlphabeticInput();

  const INITIAL_FORM_DATA = {
    email: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [general, setGeneral] = useState("");
  const [loading, setLoading] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneral("");

    if (!validate()) {
      setLoading(false);
      return;
    }

    try {
      const res = await userAPI.forgotPassword(formData);
      if (res.code === 200) {
        setGeneral(res.message || "Reset link sent to your email!");
        setFormData(INITIAL_FORM_DATA);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-funky">
      <Container fluid className="p-0 h-100">
        <Row className="g-0 min-vh-100">
          {/* Left Side: Brand Panel */}
          <Col lg={6} className="d-none d-lg-flex align-items-center justify-content-center custom-login-left-panel">
            <div className="left-panel-content">
              <span className="badge-text mb-3">SECURITY FIRST</span>
              <h1 className="left-panel-heading">LOST YOUR WAY?</h1>
              <p className="left-panel-sub">Don't worry, friend! Enter your registered email address below, and we will dispatch a reset link right to your inbox. ⚡</p>
              <div className="funky-floating-sticker">LOCKSMITH</div>
            </div>
          </Col>

          {/* Right Side: Form Box */}
          <Col lg={6} className="d-flex justify-content-center align-items-center p-4 p-md-5 bg-white">
            <div className="custom-login-formbox">
              <span className="form-badge">RECOVER</span>
              <h2 className="login-title-funky mt-3 mb-1">Forgot Password</h2>
              <p className="text-muted mb-4">No stress! Let's get you back into your account.</p>

              {/* SUCCESS MESSAGE STICKER */}
              {general && (
                <div className="funky-success-alert mb-4">
                  🎉 {general}
                </div>
              )}

              <Form method="post" onSubmit={handleSubmit}>
                {/* EMAIL FIELD */}
                <Form.Group className="mb-4" controlId="email">
                  <Form.Label className="funky-form-label mb-1">Email Address</Form.Label>
                  <div className="funky-input-wrapper">
                    <Mail size={18} className="input-icon-left" />
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="hello@vibe.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="funky-form-input"
                    />
                  </div>
                  {errors.email && (
                    <small className="text-danger d-block mt-1 fw-bold">{errors.email}</small>
                  )}
                </Form.Group>

                {/* SUBMIT BUTTON */}
                <button className="login-submit-btn-funky mb-4" type="submit" disabled={loading}>
                  {loading ? "SENDING... ⚡" : "SEND RESET LINK ⚡"}
                </button>

                {/* BACK TO LOGIN LINK */}
                <div className="text-center pt-3 border-top-funky">
                  <Link to="/users/login" className="back-to-login-link d-inline-flex align-items-center justify-content-center gap-2">
                    <ArrowLeft size={16} /> Back to Login
                  </Link>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ForgotPasswordCanvas;